import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth as useAuthContext } from '../context/AuthContext';
import api from '../services/api';
import { subscriptionService } from '../services/api';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';

const SubscriptionSuccess: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscriptionChangeType, setSubscriptionChangeType] = useState<'upgrade' | 'downgrade' | null>(null);
  const { updateUser, isAuthenticated, login } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Fonction pour vérifier si l'utilisateur est authentifié via localStorage
  const checkLocalAuthentication = (): boolean => {
    const token = localStorage.getItem('token');
    return !!token;
  };

  useEffect(() => {
    // Récupérer le type de changement d'abonnement depuis le localStorage
    const changeType = localStorage.getItem('subscription_change_type');
    if (changeType === 'upgrade' || changeType === 'downgrade') {
      setSubscriptionChangeType(changeType);
      // Nettoyer le localStorage après utilisation
      localStorage.removeItem('subscription_change_type');
    }
    
    const verifySession = async () => {
      try {
        // Récupérer l'ID de session depuis l'URL
        const searchParams = new URLSearchParams(location.search);
        const sessionId = searchParams.get('session_id');

        if (!sessionId) {
          setError('Session ID manquant. Impossible de vérifier votre abonnement.');
          setIsLoading(false);
          return;
        }

        // Vérifier si l'utilisateur est authentifié via localStorage
        const locallyAuthenticated = checkLocalAuthentication();
        
        if (!locallyAuthenticated && !isAuthenticated) {
          // Si l'utilisateur n'est pas authentifié ni localement ni via le contexte
          console.log('Utilisateur non authentifié, redirection vers login');
          const returnUrl = `${location.pathname}${location.search}`;
          navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
          return;
        } else if (locallyAuthenticated && !isAuthenticated) {
          // Si l'utilisateur est authentifié localement mais pas dans le contexte
          // Restaurer la session à partir du localStorage
          console.log('Session trouvée dans localStorage, restauration du contexte');
          const storedToken = localStorage.getItem('token');
          const storedUser = localStorage.getItem('user');
          
          if (storedToken && storedUser) {
            // Configurer le token pour les requêtes API
            api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
            
            // Restaurer le contexte d'authentification
            login({
              token: storedToken,
              user: JSON.parse(storedUser)
            });
          }
        }

        // Vérifier la session avec le backend
        const response = await subscriptionService.verifySession(sessionId);
        
        // Mettre à jour l'utilisateur dans le contexte
        if (response && updateUser) {
          updateUser(response);
        }
        
        setIsLoading(false);
      } catch (err: any) {
        console.error('Erreur lors de la vérification de la session:', err);
        setError(err.response?.data?.error || 'Une erreur est survenue lors de la vérification de votre abonnement.');
        setIsLoading(false);
      }
    };

    verifySession();
  }, [location.search, location.pathname, navigate, isAuthenticated, updateUser, login]);

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-16">
        <Card className="max-w-lg mx-auto">
          <div className="flex flex-col items-center justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
            <h2 className="text-xl font-semibold">Vérification de votre abonnement...</h2>
            <p className="text-gray-600 mt-2">Veuillez patienter pendant que nous finalisons votre abonnement.</p>
          </div>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-16">
        <Card className="max-w-lg mx-auto">
          <div className="flex flex-col items-center justify-center py-8">
            <div className="bg-red-100 p-3 rounded-full mb-4">
              <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-red-600">Erreur</h2>
            <p className="text-gray-600 mt-2 text-center">{error}</p>
            <Button onClick={handleGoToDashboard} className="mt-6">
              Retour au tableau de bord
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-16">
      <Card className="max-w-lg mx-auto">
        <div className="flex flex-col items-center justify-center py-8">
          <div className="bg-green-100 p-3 rounded-full mb-4">
            <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-green-600">Abonnement réussi !</h2>
          
          {subscriptionChangeType === 'downgrade' ? (
            <p className="text-gray-600 mt-2 text-center">
              Votre demande de changement d'abonnement a été enregistrée avec succès. <strong>Votre nouveau plan sera activé au début du prochain cycle de facturation</strong>. D'ici là, vous conservez l'accès à toutes les fonctionnalités de votre plan actuel.
            </p>
          ) : (
            <p className="text-gray-600 mt-2 text-center">
              Votre abonnement a été activé avec succès. Vous pouvez dès maintenant profiter de toutes les fonctionnalités de votre nouveau plan.
            </p>
          )}
          
          <Button onClick={handleGoToDashboard} className="mt-6">
            Accéder à mon tableau de bord
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default SubscriptionSuccess;
